import Logo from "../images/Logo";
import moment from "moment";
import { formatAddress, intToBrl } from "../utils";
import numero from "numero-por-extenso";
import _ from "lodash";
import { Company, RentContract, RentReceipt } from "../types";

const RentReceiptDisplay = ({
  receipt,
  contract,
  company,
}: {
  receipt: RentReceipt;
  contract: RentContract;
  company: Company;
}) => (
  <>
    <div className="border-b border-b-gray-900 text-center p-2 bg-gray-200 print:hidden sticky top-0">
      <button
        className="border rounded bg-white py-0.5 px-4 border-neutral-900"
        onClick={() => window.print()}
      >
        Imprimir
      </button>
    </div>
    <div className="w-[700px] m-auto">
      <div className="border-2 border-gray-800 rounded flex flex-col text-sm text-gray-900 h-[950px] mt-8 mb-16">
        <div className="flex border-b-2 border-gray-800">
          <div className="border-r-2 border-gray-800 basis-2/3 p-2 flex flex-col justify-center">
            <CompanyDisplay company={company} />
          </div>

          <div className="flex flex-col basis-1/3">
            <div className="flex flex-col border-b-2 border-gray-800 gap-2">
              <H1 className="border-b-0 text-center">Nota fatura de locação</H1>
              {!receipt.canceled && (
                <Div className="text-center text-red-800 font-semibold mb-4">
                  {`Nº ${receipt.number}`}
                </Div>
              )}
              {receipt.canceled && (
                <div className="text-center text-red-800 leading-3 mb-4">
                  <div className="line-through">{`Nº ${receipt.number}`}</div>
                  <div className="uppercase font-extrabold">Cancelada</div>
                </div>
              )}
            </div>

            <div className="flex flex-col border-b-2 border-gray-800">
              <H1>Referência</H1>
              <Div>
                {_.upperFirst(
                  moment(receipt.installment).format("MMMM / YYYY")
                )}
              </Div>
            </div>

            <div className="flex flex-col">
              <H1>Data de vencimento</H1>
              <Div>{moment.utc(receipt.expiration).format("DD/MM/YYYY")}</Div>
            </div>
          </div>
        </div>

        <div className="border-b-2 border-gray-800">
          <div className="border-b">
            <div className="uppercase font-bold py-0.5 px-2 float-left">
              Tomador
            </div>
            <Div className="ml-24">{contract.client.name}</Div>
          </div>

          <div className="border-b">
            <div className="uppercase font-bold py-0.5 px-2 float-left">
              {contract.client.type === "pj" ? "CNPJ" : "CPF"}
            </div>
            <Div className="ml-24">{contract.client.document}</Div>
          </div>

          <div className="border-b">
            <H1 className="float-left border-b-0">Endereço</H1>
            <Div className="ml-24">{formatAddress(contract.client)}</Div>
          </div>
        </div>

        <div className="border-b-2 border-gray-800">
          <H1>Descrição</H1>

          {contract.serviceAlarm && (
            <Div className="border-b">
              Locação de equipamento de alarme monitorável
            </Div>
          )}

          {contract.serviceCamera && (
            <Div className="border-b">
              Locação de câmeras ou equipamentos de CFTV
            </Div>
          )}
        </div>

        <div className="border-b-2 border-gray-800 flex-1">
          {contract.equipments.length > 0 && (
            <div className="flex">
              <H1 className="basis-1/6">Quantidade</H1>
              <H1 className="basis-5/6">Equipamento</H1>
            </div>
          )}

          {contract.equipments.map((equipment, i) => (
            <div className="flex border-b last:border-b-0" key={i}>
              <Div className="basis-1/6 text-right">{equipment.amount}</Div>
              <Div className="basis-5/6">{equipment.name}</Div>
            </div>
          ))}
        </div>

        <div className="border-b-2 border-gray-800">
          <H1>Observações</H1>
          <Div>
            {receipt.note || (
              <span className="italic text-gray-500">Sem observações</span>
            )}
          </Div>
        </div>

        <div className="flex">
          <div className="border-r-2 border-gray-800 basis-2/3">
            <H1>Valor por extenso</H1>
            <Div>
              {numero.porExtenso(receipt.amount / 100, numero.estilo.monetario)}
              <span className="text-xs text-gray-500"> * * * * * *</span>
            </Div>
          </div>

          <div className="basis-1/3">
            <div className="border-b-2 border-gray-800">
              <H1>Valor da duplicata</H1>
              <Div>{intToBrl(receipt.amount)}</Div>
            </div>

            <div>
              <H1>Valor da fatura</H1>
              <Div>{intToBrl(receipt.amount)}</Div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default RentReceiptDisplay;

const Div = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`py-0.5 px-2 ${className}`}>{children}</div>;

const H1 = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`py-0.5 px-2 border-b font-bold uppercase ${className}`}>
    {children}
  </div>
);

const CompanyDisplay = ({ company }: { company: Company }) => (
  <div className="flex flex-col gap-0.5 text-xs text-center">
    <div className="py-1.5 w-[170px] m-auto">
      <Logo />
    </div>
    <div className="font-semibold uppercase leading-4 text-sm">
      {company.name}
    </div>
    <div className="tracking-tight">{company.address}</div>
    <div>
      <span className="font-semibold">INSC. MUNICIPAL:</span> {company.im}
    </div>
    <div>
      <span className="font-semibold">CNPJ:</span> {company.cnpj}
    </div>
  </div>
);
